import { z } from 'zod';

export const ASSET_CLASSES = ['FX', 'Rates', 'Macro', 'Commodities', 'Credit', 'Equity', 'ESG'] as const;
export const assetClassSchema = z.enum(ASSET_CLASSES);
export type AssetClass = z.infer<typeof assetClassSchema>;

export const documentSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  series: z.string(),
  assetClass: assetClassSchema,
  publishDate: z.string(),
  analysts: z.array(z.string()),
  currencies: z.array(z.string()),
  pages: z.number().int(),
});
export type DocumentSummary = z.infer<typeof documentSummarySchema>;

export const citedPointSchema = z.object({ text: z.string(), page: z.number().int() });
export type CitedPoint = z.infer<typeof citedPointSchema>;

export const documentForecastSchema = z.object({
  instrument: z.string(),
  periodLabel: z.string(),
  value: z.number(),
  page: z.number().int(),
  /** One of the instruments the report is mainly about; these lead the table. */
  featured: z.boolean().default(false),
});
export type DocumentForecast = z.infer<typeof documentForecastSchema>;

export const documentDetailSchema = documentSummarySchema.extend({
  keyPoints: z.array(citedPointSchema),
  forecasts: z.array(documentForecastSchema),
  pdfUrl: z.string(),
});
export type DocumentDetail = z.infer<typeof documentDetailSchema>;

export const documentRefSchema = z.object({ id: z.string(), title: z.string(), publishDate: z.string() });
export type DocumentRef = z.infer<typeof documentRefSchema>;

export const forecastChangeSchema = z.object({
  instrument: z.string(),
  periodLabel: z.string(),
  previous: z.number().nullable(),
  current: z.number().nullable(),
  /** This report's page with the new figure. */
  page: z.number().int().nullish(),
});
export type ForecastChange = z.infer<typeof forecastChangeSchema>;

export const whatChangedSchema = z.object({
  previous: documentRefSchema.nullable(),
  changes: z.array(forecastChangeSchema),
  unchangedCount: z.number().int(),
  /** The biggest moves in one sentence, built from the numbers. */
  headline: z.string().nullish(),
  /** False when the report has no forecast table (most dailies): nothing to revise, rather than nothing revised. */
  hasForecasts: z.boolean().default(true),
});
export type WhatChanged = z.infer<typeof whatChangedSchema>;

/** Calls, drivers and risks, each cited to a page and checked. Null when no model is set up. */
export const briefSchema = z
  .object({
    calls: z.array(citedPointSchema),
    drivers: z.array(citedPointSchema),
    risks: z.array(citedPointSchema),
    changeReason: citedPointSchema.nullable(),
    model: z.string(),
  })
  .nullable();
export type Brief = z.infer<typeof briefSchema>;
